import { bool, func, node, object, string } from 'prop-types'
import { Children, cloneElement } from 'react'
import { kuiperClassNames } from '../../utils'
import TogglePane from '../TogglePane'

import './styles.scss'

const changer = (name, value, onChange, newValue) =>
  onChange({
    ...value,
    [name]: newValue,
  })

const intercept = (names, value, onChange, toggleable, child) => {
  if (!child) {
    return null
  }
  const name = child.props.name
  if (!name) {
    throw new Error(
      `All inputs in a collection must have a name. Check the ${child.type} inputs in this collection.`
    )
  }
  if (names.has(name)) {
    console.warn(
      `Two inputs with the same name are being handled by an input collection. This could lead to unexpected behavior. Check all inputs named ${name}.`
    )
  }
  if (name === 'wrapper') {
    const childProps = child.props
    return Children.map(
      childProps.children,
      intercept.bind(this, names, value, onChange, childProps.toggleable)
    )
  }

  names.add(name)
  const xtraProps = {
    onChange: changer.bind(this, name, value, onChange),
    value: value[name],
  }
  const clonedChild = cloneElement(child, xtraProps)
  if (toggleable) {
    const header = (
      <span className={kuiperClassNames('header-label')}>{name}</span>
    )
    return <TogglePane header={header}>{clonedChild}</TogglePane>
  }
  return clonedChild
}

/**
 * Automatically injects an `onChange` callback for each child. Each child must
 * have a `name` prop; if the `name` is `"wrapper"` the behavior is passed to
 * the child's own children, which must each have a `name` prop.
 *
 * The `name` prop is a key in the `value` object. When the `onChange` callback
 * fires it returns `value` with the `name` key set to the value `onChange` was
 * called with.
 *
 * @note If a child has its own `onChange` callback you may want to wrap the
 *   `onChange` callback this injects.
 *
 * @example
 * // `@opallabs/kuiper` `<Select>`
 * <Select
 *   // ...
 *   onChange={(option) => onChange(option.value)}
 * />
 */
const InputCollection = ({
  children,
  className,
  collectionHeader,
  dangerValidation = false,
  onChange,
  toggleable,
  shouldValidate = false,
  valid = false,
  value = {},
}) => {
  const names = new Set()
  const validationClasses = {
    valid,
    danger: dangerValidation,
    shouldValidate,
  }
  return (
    <div
      className={kuiperClassNames('input input-collection-wrapper', className, {
        danger: dangerValidation,
      })}
    >
      <div className={kuiperClassNames('header-label', validationClasses)}>
        {collectionHeader}
      </div>
      <div className={kuiperClassNames('input-collection')}>
        {Children.map(
          children,
          intercept.bind(this, names, value, onChange, toggleable)
        )}
      </div>
    </div>
  )
}

InputCollection.propTypes = {
  children: node,
  className: string,
  collectionHeader: node,
  dangerValidation: bool,
  onChange: func,
  shouldValidate: bool,
  toggleable: bool,
  valid: bool,
  value: object,
}

export default InputCollection
